import React, { useEffect, useRef, useState } from "react";
import Parse from "../parseClient";
import { logOutUser, saveUserScores, getCurrentMonthScoreField, validateNameUniqueness } from "../utils/authService";
import AvatarCropModal from "./AvatarCropModal";
import "./ProfileModal.css";

// FEATURE: UNIQUE_NAME_VALIDATION
const UNIQUE_NAME_VALIDATION_ENABLED = true;
// END FEATURE: UNIQUE_NAME_VALIDATION

function ProfileModal({
  onClose,
  profileTexts,
  currentUser,
  profileDefault,
  onUserUpdated,
  onUserLoggedOut,
  avatarCropLabels,
}) {
  const [profileName, setProfileName] = useState("");
  const [originalProfileName, setOriginalProfileName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(profileDefault);
  const avatarInputRef = useRef(null);
  const [avatarCropState, setAvatarCropState] = useState({
    visible: false,
    imageSrc: null,
  });
  // FEATURE: UNIQUE_NAME_VALIDATION
  const [nameValidation, setNameValidation] = useState({
    checking: false,
    available: null,
    suggestion: null,
  });
  // END FEATURE: UNIQUE_NAME_VALIDATION

  useEffect(() => {
    if (!currentUser) return;
    const currentName = currentUser.get("name") || "";
    setProfileName(currentName);
    setOriginalProfileName(currentName);
    const avatarFile = currentUser.get("avatar");
    if (avatarFile && typeof avatarFile.url === "function") {
      const url = avatarFile.url();
      setAvatarUrl(url || profileDefault);
    } else {
      setAvatarUrl(profileDefault);
    }
    setErrorMessage("");
    setSuccessMessage("");
    // FEATURE: UNIQUE_NAME_VALIDATION
    setNameValidation({ checking: false, available: null, suggestion: null });
    // END FEATURE: UNIQUE_NAME_VALIDATION
  }, [currentUser, profileDefault]);

  // FEATURE: UNIQUE_NAME_VALIDATION
  // Debounced name validation during typing
  useEffect(() => {
    if (!UNIQUE_NAME_VALIDATION_ENABLED || !currentUser) {
      return;
    }

    const trimmedName = profileName.trim();
    
    // Skip validation if name is too short, empty, or unchanged
    if (trimmedName.length < 2 || trimmedName === originalProfileName.trim()) {
      setNameValidation({ checking: false, available: null, suggestion: null });
      return;
    }

    // Debounce timer
    const timer = setTimeout(async () => {
      setNameValidation(prev => ({ ...prev, checking: true }));
      
      try {
        const result = await validateNameUniqueness(trimmedName, currentUser.id);
        setNameValidation({
          checking: false,
          available: result.available,
          suggestion: result.suggestion,
        });
      } catch (error) {
        console.warn("Name validation error:", error);
        setNameValidation({
          checking: false,
          available: null,
          suggestion: null,
        });
      }
    }, 600); // 600ms debounce
    
    return () => clearTimeout(timer);
  }, [profileName, originalProfileName, currentUser]);
  // END FEATURE: UNIQUE_NAME_VALIDATION

  const handleDismiss = () => {
    setAvatarCropState({ visible: false, imageSrc: null });
    setErrorMessage("");
    setSuccessMessage("");
    onClose();
  };

  const handleSave = async () => {
    if (!currentUser) return;
    const trimmedName = profileName.trim();
    if (!trimmedName) {
      setErrorMessage(profileTexts.emptyNameError);
      setSuccessMessage("");
      return;
    }
    
    // FEATURE: UNIQUE_NAME_VALIDATION
    if (UNIQUE_NAME_VALIDATION_ENABLED) {
      // Final validation before save
      const validation = await validateNameUniqueness(trimmedName, currentUser.id);
      if (!validation.available) {
        const suggestionText = validation.suggestion 
          ? (profileTexts.nameTakenSuggestion || ` Try "${validation.suggestion}" instead.`)
              .replace("{suggestion}", validation.suggestion)
          : (profileTexts.nameTaken || "This name is already taken.");
        setErrorMessage(suggestionText);
        setSuccessMessage("");
        return;
      }
    }
    // END FEATURE: UNIQUE_NAME_VALIDATION
    
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      currentUser.set("name", trimmedName);
      // Avatar is already uploaded when crop was confirmed, no need to upload again
      await currentUser.save();
      const refreshedUser = Parse.User.current();
      if (refreshedUser) {
        onUserUpdated?.(refreshedUser);
        const newName = refreshedUser.get("name") || "";
        setOriginalProfileName(newName);
        setProfileName(newName);
        const avatarFile = refreshedUser.get("avatar");
        if (avatarFile && typeof avatarFile.url === "function") {
          setAvatarUrl(avatarFile.url() || profileDefault);
        } else {
          setAvatarUrl(profileDefault);
        }
      }
      setSuccessMessage(profileTexts.saveSuccess);
    } catch (error) {
      console.error("Failed to save profile:", error);
      setErrorMessage(profileTexts.genericError);
    } finally {
      setIsAvatarUploading(false);
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logOutUser();
      onUserLoggedOut?.();
    } catch (error) {
      console.error("Failed to log out:", error);
      setErrorMessage(profileTexts.logoutError);
      setSuccessMessage("");
      return;
    }
    handleDismiss();
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    
    // Show confirmation prompt
    const confirmed = window.confirm(profileTexts.deleteAccountConfirm || "Are you sure?");
    if (!confirmed) return;
    
    try {
      // Set all scores to 0
      await saveUserScores({
        score: 0,
        streak: 0,
        maxStreak: 0,
        numbersPlayed: 0,
        lastWonId: null,
        lastScore: null,
        lastPlayedId: null,
      });
      
      // Reset monthly scores
      const { fieldName: currentFieldName } = getCurrentMonthScoreField();
      currentUser.set(currentFieldName, 0);
      currentUser.set(currentFieldName === "scoreMonthEven" ? "scoreMonthOdd" : "scoreMonthEven", 0);
      
      // Prefix name with "deleted"
      const currentName = currentUser.get("name") || "";
      const deletedName = currentName.startsWith("deleted") ? currentName : `deleted ${currentName}`;
      currentUser.set("name", deletedName);
      
      // Save the changes
      await currentUser.save();
      
      // Logout the user
      await logOutUser();
      onUserLoggedOut?.();
      handleDismiss();
    } catch (error) {
      console.error("Failed to delete account:", error);
      setErrorMessage(profileTexts.deleteAccountError || "Failed to delete account. Please try again.");
      setSuccessMessage("");
    }
  };

  const handleAvatarButtonClick = () => {
    const input = avatarInputRef.current;
    if (input) {
      input.click();
    }
  };

  const handleAvatarFileChange = (event) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrorMessage(profileTexts.invalidImageError || "Please select an image file.");
        event.target.value = "";
        return;
      }
      
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setErrorMessage(profileTexts.imageTooLargeError || "Image is too large. Please select an image smaller than 10MB.");
        event.target.value = "";
        return;
      }
      
      const reader = new FileReader();
      reader.onerror = () => {
        console.error("Failed to read image file");
        setErrorMessage(profileTexts.imageReadError || "Failed to read image. Please try again.");
        event.target.value = "";
      };
      reader.onload = () => {
        if (reader.result) {
          setAvatarCropState({
            visible: true,
            imageSrc: reader.result,
          });
        }
      };
      reader.readAsDataURL(file);
      event.target.value = "";
    } catch (error) {
      console.error("Error handling avatar file change:", error);
      setErrorMessage(profileTexts.genericError || "An error occurred. Please try again.");
      event.target.value = "";
    }
  };

  const handleAvatarCropCancel = () => {
    setAvatarCropState({ visible: false, imageSrc: null });
  };

  const handleAvatarCropConfirm = async (blob) => {
    if (!blob || !currentUser) {
      setAvatarCropState({ visible: false, imageSrc: null });
      return;
    }
    
    setAvatarCropState({ visible: false, imageSrc: null });
    setIsAvatarUploading(true);
    setErrorMessage("");
    
    // Create object URL for immediate preview
    const objectUrl = URL.createObjectURL(blob);
    setAvatarUrl(objectUrl);
    
    try {
      // Upload avatar directly to Parse
      const fileName = `avatar-${Date.now()}.png`;
      const file = new File([blob], fileName, {
        type: blob.type || "image/png",
      });
      const avatarFile = new Parse.File(fileName, file);
      await avatarFile.save();
      
      // Update user with new avatar
      currentUser.set("avatar", avatarFile);
      await currentUser.save();
      
      // Refresh user and update avatar URL
      const refreshedUser = Parse.User.current();
      if (refreshedUser) {
        onUserUpdated?.(refreshedUser);
        const avatarFileResult = refreshedUser.get("avatar");
        if (avatarFileResult && typeof avatarFileResult.url === "function") {
          const remoteUrl = avatarFileResult.url();
          setAvatarUrl(remoteUrl || profileDefault);
        } else {
          setAvatarUrl(profileDefault);
        }
      }
      
      setSuccessMessage(profileTexts.avatarUploadSuccess || "Photo uploaded successfully");
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error("Failed to upload avatar:", error);
      setErrorMessage(profileTexts.avatarUploadError || "Failed to upload photo. Please try again.");
      // Revert to previous avatar on error
      const avatarFile = currentUser.get("avatar");
      if (avatarFile && typeof avatarFile.url === "function") {
        setAvatarUrl(avatarFile.url() || profileDefault);
      } else {
        setAvatarUrl(profileDefault);
      }
      // Clean up object URL on error
      URL.revokeObjectURL(objectUrl);
    } finally {
      setIsAvatarUploading(false);
    }
  };

  const isSaveDisabled =
    isSaving ||
    profileName.trim() === originalProfileName.trim();

  return (
    <>
      <div className="profile-overlay" onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          handleDismiss();
        }
      }} />
      <div className="profile-modal" role="dialog" aria-modal="true">
        <button
          className="profile-close-button"
          onClick={handleDismiss}
          aria-label={profileTexts.close}
        >
          ✕
        </button>

        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*"
          // capture="environment"
          style={{ display: "none" }}
          onChange={handleAvatarFileChange}
        />

        {avatarCropState.visible && avatarCropState.imageSrc && (
          <AvatarCropModal
            imageSrc={avatarCropState.imageSrc}
            onCancel={handleAvatarCropCancel}
            onConfirm={handleAvatarCropConfirm}
            labels={avatarCropLabels}
          />
        )}

        <div className="profile-header">
          <button
            type="button"
            className="profile-avatar-button"
            onClick={handleAvatarButtonClick}
            aria-label={profileTexts.changeAvatarLabel || "Change avatar"}
            disabled={isAvatarUploading}
          >
            <img
              src={avatarUrl || profileDefault}
              alt="Profile"
              className="profile-avatar"
            />
            <span className="profile-avatar-edit">
              {profileTexts.changeAvatarLabel || "Change"}
            </span>
            {isAvatarUploading && (
              <span className="profile-avatar-loading">…</span>
            )}
          </button>
          <h2 className="profile-title">{profileTexts.title}</h2>
        </div>

        <div className="profile-field">
          <label className="profile-label" htmlFor="profile-name-input">
            {profileTexts.nameLabel}
          </label>
          <input
            id="profile-name-input"
            type="text"
            value={profileName}
            onChange={(event) => setProfileName(event.target.value)}
            className="profile-input"
            autoComplete="name"
          />
          {/* FEATURE: UNIQUE_NAME_VALIDATION */}
          {UNIQUE_NAME_VALIDATION_ENABLED && profileName.trim().length >= 2 && profileName.trim() !== originalProfileName.trim() && (
            <div className="profile-name-validation">
              {nameValidation.checking && (
                <span className="profile-name-validation-checking">
                  {profileTexts.checkingName || "Checking..."}
                </span>
              )}
              {!nameValidation.checking && nameValidation.available === false && (
                <div className="profile-name-validation-error">
                  <span>{profileTexts.nameTaken || "This name is already taken."}</span>
                  {nameValidation.suggestion && (
                    <button
                      type="button"
                      className="profile-name-suggestion-button"
                      onClick={() => {
                        setProfileName(nameValidation.suggestion);
                        setNameValidation({ checking: false, available: null, suggestion: null });
                      }}
                    >
                      {profileTexts.useSuggestion || "Use"} "{nameValidation.suggestion}"
                    </button>
                  )}
                </div>
              )}
              {!nameValidation.checking && nameValidation.available === true && (
                <span className="profile-name-validation-success">
                  ✓ {profileTexts.nameAvailable || "Available"}
                </span>
              )}
            </div>
          )}
          {/* END FEATURE: UNIQUE_NAME_VALIDATION */}
        </div>

        <div className="profile-field">
          <span className="profile-label">{profileTexts.emailLabel}</span>
          <span className="profile-email">
            {currentUser.get("email") ||
              currentUser.get("username") ||
              ""}
          </span>
        </div>

        {errorMessage && (
          <div className="profile-message profile-message--error">
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className="profile-message profile-message--success">
            {successMessage}
          </div>
        )}

        <div className="profile-actions">
          <button
            className="profile-save-button"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {isSaving ? profileTexts.saving : profileTexts.save}
          </button>
          <button className="profile-logout-button" onClick={handleLogout}>
            {profileTexts.logout}
          </button>
          <button className="profile-delete-account-button" onClick={handleDeleteAccount}>
            {profileTexts.deleteAccount || "Delete account"}
          </button>
        </div>
      </div>
    </>
  );
}

export default ProfileModal;